import React, { useEffect, useRef } from 'react';

import { BarChart, LineChart } from 'echarts/charts';
import { AriaComponent, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import {
  MEASURES,
  type Measure,
  type Report,
  axisFormat,
  chartRows,
  metricFormat,
  seriesStyle,
} from './presentationModel';

echarts.use([
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  AriaComponent,
  DataZoomComponent,
  CanvasRenderer,
]);
const escape = (value: unknown) =>
  String(value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
  );
/** Shared renderer: identical data, null gaps and interaction in native and standalone hosts. */
export default function FidelityChart({
  report,
  measure = 'clicks',
  stacked = false,
  height = 350,
}: {
  report: Report;
  measure?: Measure;
  stacked?: boolean;
  height?: number;
}) {
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!container.current) {
      return;
    }
    const node = container.current,
      chart = echarts.init(node, undefined, { renderer: 'canvas' }),
      names = stacked ? report.products : report.series,
      rows = stacked ? report.clickTimeline : chartRows(report, measure),
      definition = MEASURES.find((m) => m.key === measure),
      many = names.length > 8;
    chart.setOption({
      animation: false,
      backgroundColor: '#FFFFFF',
      textStyle: { fontFamily: getComputedStyle(node).fontFamily, fontSize: 11, color: '#0F0C08' },
      aria: {
        enabled: true,
        label: {
          description:
            (stacked ? 'Stacked clicks by product' : definition.label) +
            ' over reporting send dates. Missing values are Unknown. Values and all series are available in CSV.',
        },
        decal: { show: stacked },
      },
      grid: { left: 78, right: 22, top: names.length > 1 ? 58 : 24, bottom: 67, containLabel: false },
      legend: {
        show: names.length > 1,
        type: 'scroll',
        top: 8,
        left: 12,
        right: 15,
        textStyle: { fontSize: 11, color: '#0F0C08' },
        pageTextStyle: { color: '#0F0C08' },
        tooltip: { show: true },
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        backgroundColor: '#FFFFFF',
        borderColor: '#0F464F',
        textStyle: { color: '#0F0C08', fontSize: 12 },
        formatter: (raw: unknown) => {
          const list = (Array.isArray(raw) ? raw : [raw]) as Array<{
            axisValue: string;
            seriesName: string;
            value: unknown;
          }>;
          return (
            '<b>' +
            escape(list[0]?.axisValue ?? '') +
            '</b><br>' +
            list
              .slice(0, 30)
              .map(
                (p) =>
                  escape(p.seriesName) +
                  ': ' +
                  escape(metricFormat(typeof p.value === 'number' ? p.value : null, measure)),
              )
              .join('<br>') +
            (list.length > 30 ? '<br>Further series available in CSV' : '')
          );
        },
      },
      xAxis: {
        type: 'category',
        data: rows.map((r) => r.name),
        boundaryGap: stacked,
        axisLine: { lineStyle: { color: '#77736D' } },
        axisTick: { alignWithLabel: true },
        axisLabel: {
          color: '#0F0C08',
          hideOverlap: true,
          formatter: (s: string) => (/^\d{4}-/.test(s) ? s.slice(5) : s),
        },
        name: 'Reporting send date',
        nameLocation: 'middle',
        nameGap: 32,
        nameTextStyle: { color: '#0F0C08', fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#0F0C08', formatter: (v: number) => axisFormat(v, measure) },
        name: definition.kind === 'money' ? 'AUD' : definition.kind === 'percent' ? 'Rate' : 'Count',
        nameTextStyle: { color: '#0F0C08' },
        splitLine: { lineStyle: { color: '#E5E2DA' } },
        min: definition.kind === 'percent' ? 0 : undefined,
      },
      dataZoom:
        rows.length > 35
          ? [
              { type: 'inside', filterMode: 'none' },
              {
                type: 'slider',
                height: 13,
                bottom: 3,
                filterMode: 'none',
                borderColor: '#D3D0C7',
                fillerColor: 'rgba(15,70,79,.12)',
                handleStyle: { color: '#0F464F' },
                textStyle: { color: '#0F0C08' },
              },
            ]
          : [],
      series: names.map((name, i) => {
        const style = seriesStyle(i);
        return {
          name,
          type: stacked ? 'bar' : 'line',
          stack: stacked ? 'clicks' : undefined,
          data: rows.map((r) => r['s' + i] ?? null),
          connectNulls: false,
          smooth: false,
          showSymbol: rows.length < 3,
          symbolSize: 5,
          emphasis: { focus: 'series' },
          lineStyle: {
            color: style.colour,
            width: i % 6 === 1 ? 4 : 2,
            type: style.dash ? 'dashed' : 'solid',
            shadowColor: i % 6 === 1 || i % 6 === 3 ? '#0F464F' : undefined,
            shadowBlur: i % 6 === 1 || i % 6 === 3 ? 1 : 0,
          },
          itemStyle: {
            color: style.colour,
            borderColor: '#0F464F',
            borderWidth: stacked ? 0.6 : 1,
            decal: stacked
              ? {
                  symbol: ['rect', 'circle', 'triangle'][Math.floor(i / 6) % 3],
                  dashArrayX: [1, 0],
                  dashArrayY: i < 6 ? [0, 8] : [2, 5],
                  rotation: i % 2 ? Math.PI / 4 : 0,
                  color: 'rgba(15,70,79,.4)',
                }
              : undefined,
          },
          large: many && stacked,
        };
      }),
    });
    const resize = new ResizeObserver(() => chart.resize());
    resize.observe(node);
    return () => {
      resize.disconnect();
      chart.dispose();
    };
  }, [report, measure, stacked]);
  const count = stacked ? report.clickRows.length : report.sourceRows.length;
  return count ? (
    <div
      ref={container}
      role="img"
      aria-label={
        (stacked ? 'Stacked clicks by product' : MEASURES.find((m) => m.key === measure).label) +
        ' over reporting send dates. Values and all series available in CSV.'
      }
      style={{ height, width: '100%', minWidth: 0, background: '#FFFFFF' }}
    />
  ) : (
    <p style={{ padding: 30 }}>Unknown · no source rows for this scope.</p>
  );
}
